import type { Snapshot } from "./api";
import { hasLeitstelleOnMap } from "./worldChecks";
import {
  isWrongCrewAssignStation,
  opsUnderstaffedVehicleCount,
} from "./crewStation";
import { RECRUIT_INTERVIEW_MS } from "./hrConstants";
import type { TourTarget } from "./tutorialTour";

export type StoryGuideAction =
  | "open_build"
  | "open_progress"
  | "open_citizen"
  | "open_incident"
  | "open_station"
  | "open_station_crew"
  | "open_station_shop"
  | "open_stations"
  | "open_catalog"
  | "open_hr"
  | "open_hr_recruit"
  | "open_hr_pool"
  | "open_hr_assign"
  | "none";

export type CoachUiState = {
  panel: string | null;
  selectedBuildingId: string | null;
  stationModalTab?: string | null;
  hrModalTab?: string | null;
  hrAssignBuildingId?: string | null;
  incidentTab?: string | null;
  mapTactical?: { incidentId: string; tool: string | null } | null;
};

export type CoachGuideStep = {
  target: TourTarget;
  action: StoryGuideAction;
};

function preferredStationId(snap: Snapshot): string | undefined {
  const org = snap.world.startOrg ?? "fire";
  const kind =
    org === "police" ? "police_station" : org === "ems" ? "ems_station" : "fire_station";
  return (
    snap.buildings.find((b) => b.kind === kind)?.id ??
    snap.buildings.find((b) =>
      ["fire_station", "police_station", "ems_station", "thw"].includes(b.kind),
    )?.id
  );
}

function stationTabIs(ui: CoachUiState, tab: string): boolean {
  return ui.stationModalTab === tab;
}

function hrTabIs(ui: CoachUiState, tab: string): boolean {
  return ui.hrModalTab === tab;
}

/** Verwaltungs-Modal mit oberer Tab-Leiste (ManageShell) — nicht Station-Detail. */
const MANAGE_SHELL_PANELS = new Set([
  "verwaltung",
  "hr",
  "fleet",
  "finance",
  "stations",
  "citizen",
  "rewards",
  "catalog",
  "build",
  "progress",
]);

function isManageShellOpen(ui: CoachUiState): boolean {
  return ui.panel != null && MANAGE_SHELL_PANELS.has(ui.panel);
}

function understaffedVehicleCount(snap: Snapshot): number {
  return opsUnderstaffedVehicleCount(snap);
}

/** Mehrstufig: Dock → Modal/Liste → Tab → Aktion — je nach offenem Panel. */
export function resolveCoachGuideStep(
  missionId: string,
  snap: Snapshot,
  ui: CoachUiState,
): CoachGuideStep | null {
  const guide = STORY_MISSION_GUIDES[missionId];
  if (!guide || guide.ready(snap)) return null;

  switch (missionId) {
    case "ch1_besatzung": {
      if (ui.panel === "station" && stationTabIs(ui, "crew")) {
        return { target: "station-crew-fill", action: "none" };
      }
      if (ui.panel === "station") {
        return { target: "station-nav-crew", action: "open_station_crew" };
      }
      if (ui.panel === "stations") {
        return { target: "station-list", action: "none" };
      }
      if (isManageShellOpen(ui)) {
        return { target: "dock-station", action: "open_stations" };
      }
      return { target: "dock-station", action: "open_stations" };
    }
    case "ch1_fuhrpark": {
      if (ui.panel === "station" && stationTabIs(ui, "shop")) {
        return { target: "station-shop", action: "none" };
      }
      if (ui.panel === "station") {
        return { target: "station-nav-shop", action: "open_station_shop" };
      }
      if (ui.panel === "stations") {
        return { target: "station-list", action: "none" };
      }
      if (isManageShellOpen(ui)) {
        return { target: "dock-station", action: "open_stations" };
      }
      return { target: "dock-station", action: "open_stations" };
    }
    case "ch1_personal_werben": {
      if (ui.panel === "hr" && hrTabIs(ui, "recruit")) {
        return { target: "hr-recruit", action: "none" };
      }
      if (ui.panel === "hr") {
        return { target: "hr-recruit-tab", action: "open_hr_recruit" };
      }
      if (isManageShellOpen(ui)) {
        return { target: "hr", action: "open_hr_recruit" };
      }
      return { target: "hr", action: "open_hr_recruit" };
    }
    case "ch1_personal_gespraech": {
      const applicants = snap.applicants ?? [];
      const poolReady = applicants.some((a) => (a.recruitStatus ?? "applied") === "applied");
      if (ui.panel === "hr" && hrTabIs(ui, "pool") && poolReady) {
        return { target: "hr-applicant-invite", action: "none" };
      }
      if (ui.panel === "hr" && hrTabIs(ui, "pool")) {
        return { target: "hr-pool", action: "none" };
      }
      if (ui.panel === "hr") {
        return { target: "hr-pool-tab", action: "open_hr_pool" };
      }
      if (isManageShellOpen(ui)) {
        return { target: "hr", action: "open_hr_pool" };
      }
      return { target: "hr", action: "open_hr_pool" };
    }
    case "ch1_personal_einstellen": {
      const ready = (snap.applicants ?? []).some((a) => a.recruitStatus === "interview_done");
      if (ui.panel === "hr" && hrTabIs(ui, "pool") && ready) {
        return { target: "hr-applicant-hire", action: "none" };
      }
      if (ui.panel === "hr" && hrTabIs(ui, "pool")) {
        return { target: "hr-pool", action: "none" };
      }
      if (ui.panel === "hr") {
        return { target: "hr-pool-tab", action: "open_hr_pool" };
      }
      if (isManageShellOpen(ui)) {
        return { target: "hr", action: "open_hr_pool" };
      }
      return { target: "hr", action: "open_hr_pool" };
    }
    case "ch1_besatzung_zweites_fz": {
      if (ui.panel === "hr" && hrTabIs(ui, "assign")) {
        if (
          isWrongCrewAssignStation(
            snap,
            ui.selectedBuildingId,
            ui.hrAssignBuildingId,
          )
        ) {
          return { target: "station-crew-picker", action: "none" };
        }
        return { target: "station-crew-fill", action: "none" };
      }
      if (ui.panel === "hr") {
        return { target: "hr-assign-tab", action: "open_hr_assign" };
      }
      if (isManageShellOpen(ui)) {
        return { target: "hr", action: "open_hr_assign" };
      }
      return { target: "hr", action: "open_hr_assign" };
    }
    case "ch1_taktik": {
      const mapCount = (snap.world.story as { mapTacticalCount?: number })?.mapTacticalCount ?? 0;
      if (mapCount >= 1) return null;
      if (ui.mapTactical?.tool) {
        return { target: "incident-map-tools", action: "none" };
      }
      if (ui.panel === "incident" && ui.incidentTab === "karte") {
        return { target: "incident-map-tools", action: "none" };
      }
      if (ui.panel === "incident") {
        return { target: "incident-map", action: "none" };
      }
      const tacticalInc = snap.incidents.find(
        (i) =>
          (i.status === "open" || i.status === "active") &&
          /Verfolg|Fahndung|Groß|Volksfest|Sammelstelle|Wasser|VU|Absperr|Seebrand|Wasserrettung|MANV|Massenanfall/i.test(
            i.title + (i.summary ?? ""),
          ),
      );
      if (tacticalInc) {
        return { target: "incident-list", action: "open_incident" };
      }
      return { target: "incident-list", action: "none" };
    }
    default:
      return { target: guide.target, action: guide.action };
  }
}

export { preferredStationId };

/** Reihenfolge der Auto-Führung — für Schritt-Zähler „Schritt X von Y“. */
const GUIDE_STEP_ORDER: Partial<Record<string, TourTarget[]>> = {
  ch1_besatzung: ["dock-station", "station-list", "station-nav-crew", "station-crew-fill"],
  ch1_fuhrpark: ["dock-station", "station-list", "station-nav-shop", "station-shop"],
  ch1_personal_werben: ["hr", "hr-recruit-tab", "hr-recruit"],
  ch1_personal_gespraech: ["hr", "hr-pool-tab", "hr-pool", "hr-applicant-invite"],
  ch1_personal_einstellen: ["hr", "hr-pool-tab", "hr-pool", "hr-applicant-hire"],
  ch1_besatzung_zweites_fz: ["hr", "hr-assign-tab", "station-crew-picker", "station-crew-fill"],
  ch1_taktik: ["incident-list", "incident-map", "incident-map-tools"],
};

/** Kurztext pro Guide-Schritt — Coach zeigt automatisch, kein „Zeigen“-Klick nötig. */
const GUIDE_STEP_HINTS: Partial<Record<string, Partial<Record<TourTarget, string>>>> = {
  ch1_besatzung: {
    "dock-station": "Wachen öffnen — dann Ihre Wache in der Liste wählen.",
    "station-list": "Ihre Wache antippen.",
    "station-nav-crew": "Tab Besatzung — öffnet automatisch.",
    "station-crew-fill": "Personal den Fahrzeugen zuweisen (Drag & Drop oder Auto).",
  },
  ch1_fuhrpark: {
    "dock-station": "Wachen öffnen — dann Fahrzeugshop.",
    "station-list": "Ihre Wache antippen.",
    "station-nav-shop": "Tab Fahrzeugshop — öffnet automatisch.",
    "station-shop": "Zweites Fahrzeug kaufen — Budget reicht für TSF oder LF.",
  },
  ch1_personal_werben: {
    hr: "Personal öffnen.",
    "hr-recruit-tab": "Tab Werbung — öffnet automatisch.",
    "hr-recruit": "Werbekampagne starten — Kosten gehen vom Budget.",
  },
  ch1_personal_gespraech: {
    hr: "Personal öffnen.",
    "hr-pool-tab": "Tab Bewerber — öffnet automatisch.",
    "hr-pool": "Bewerber in der Liste.",
    "hr-applicant-invite": "Zum Gespräch einladen — danach läuft das Gespräch (~1 Min.).",
  },
  ch1_personal_einstellen: {
    hr: "Personal öffnen.",
    "hr-pool-tab": "Tab Bewerber — öffnet automatisch.",
    "hr-pool": "Kandidaten nach abgeschlossenem Gespräch.",
    "hr-applicant-hire": "Annehmen & einstellen.",
  },
  ch1_besatzung_zweites_fz: {
    hr: "Unten Personal öffnen.",
    "hr-assign-tab": "Tab Besatzung — dort Fahrzeuge und freie Kräfte sehen.",
    "station-crew-picker":
      "Schritt 1: Oben die richtige Einsatzwache wählen (Feuerwache — nicht Leitstelle).",
    "station-crew-fill":
      "Schritt 2: „Wache befüllen“ — oder Person antippen, dann unterbesetztes Fahrzeug.",
  },
  ch1_taktik: {
    "incident-list":
      "Einsatz mit Karten-Taktik öffnen (Verfolgung, Großlage, Wasser, VU … — Tab Karte).",
    "incident-map":
      "Tab Karte — Werkzeug passend zum Profil (Nagelgurt, Sammelstelle, Hydrant/Saugstelle …).",
    "incident-map-tools":
      "Auf der Karte tippen — Maßnahme auf Straße, Gewässer oder Evakuierungsbereich.",
  },
};

export type CoachWaitState = {
  kind: "interview" | "applicant_arrival" | "vehicle_travel";
  message: string;
  /** 0–1 Fortschritt, wenn bekannt */
  progress?: number;
  /** Verbleibende Sekunden */
  secondsLeft?: number;
  parallelHint: string;
};

export function detectCoachWaitState(
  missionId: string | undefined,
  snap: Snapshot,
): CoachWaitState | null {
  const applicants = snap.applicants ?? [];
  const now = Date.now();

  const interviewing = applicants.find((a) => (a.recruitStatus ?? "applied") === "interviewing");
  if (interviewing?.interviewUntil) {
    const until = new Date(interviewing.interviewUntil as string | number | Date).getTime();
    const leftMs = Math.max(0, until - now);
    if (leftMs > 0) {
      const total = RECRUIT_INTERVIEW_MS;
      return {
        kind: "interview",
        message: `Bewerbungsgespräch mit ${interviewing.name} — noch ~${Math.ceil(leftMs / 1000)} Sek.`,
        progress: 1 - leftMs / total,
        secondsLeft: Math.ceil(leftMs / 1000),
        parallelHint:
          "Gespräch läuft im Hintergrund — rechts Einsätze alarmieren und abschließen. Das Ergebnis erscheint automatisch.",
      };
    }
  }

  if (missionId === "ch1_personal_werben") {
    const pending = applicants.filter(
      (a) => new Date(a.createdAt as string | number | Date).getTime() > now,
    );
    if (pending.length > 0) {
      const next = pending.reduce((best, a) => {
        const t = new Date(a.createdAt as string | number | Date).getTime();
        return t < best ? t : best;
      }, Infinity);
      const leftMs = Math.max(0, next - now);
      return {
        kind: "applicant_arrival",
        message: `Bewerber unterwegs — erster Eingang in ~${Math.ceil(leftMs / 1000)} Sek.`,
        secondsLeft: Math.ceil(leftMs / 1000),
        parallelHint:
          "Werbung wirkt — Bewerber kommen gestaffelt. Bis dahin Einsätze weiter bearbeiten.",
      };
    }
  }

  const enroute = snap.vehicles.filter((v) => v.status === "enroute").length;
  if (
    enroute > 0 &&
    (missionId === "ch1_erste_lagen" ||
      missionId === "ch1_zwischen_lagen" ||
      missionId === "ch1_weiter_ueben")
  ) {
    return {
      kind: "vehicle_travel",
      message: `${enroute} Einheit${enroute === 1 ? "" : "en"} unterwegs — Anfahrt dauert je nach Entfernung.`,
      parallelHint: "Weitere Einsätze können parallel alarmiert werden.",
    };
  }

  return null;
}

export function coachGuideTargetIndex(missionId: string, target: TourTarget): number {
  const order = GUIDE_STEP_ORDER[missionId];
  if (!order) return -1;
  return order.indexOf(target);
}

export function coachGuideStepProgress(
  missionId: string,
  step: CoachGuideStep | null,
): { current: number; total: number } | null {
  const order = GUIDE_STEP_ORDER[missionId];
  if (!order || !step) return null;
  const idx = order.indexOf(step.target);
  if (idx < 0) return { current: 1, total: order.length };
  return { current: idx + 1, total: order.length };
}

export function coachGuideStepHint(
  missionId: string,
  step: CoachGuideStep | null,
): string | null {
  if (!step) return null;
  return GUIDE_STEP_HINTS[missionId]?.[step.target] ?? null;
}

export type StoryMissionGuideDef = {
  target: TourTarget;
  action: StoryGuideAction;
  /** Leer = kein Button (z. B. reine Einsatz-Grind-Aufträge) */
  ctaLabel?: string;
  waitHint: string;
  ready: (snap: Snapshot) => boolean;
  /** Nur „Einsätze abschließen“ — knappe Anzeige, kein Öffnen-CTA */
  resolveGrind?: boolean;
  /** Zeigen/Hinweis — kein Panel öffnen, nur Pointer + Kurztext */
  guideOnly?: boolean;
  /**
   * Blauer CoachPointer (fixed overlay). Default: an, außer bei Einsatzlisten-Zielen
   * (üben/warten/grind) — Hinweis bleibt im Coach-Panel, nicht über Rang/Top-Chrome.
   */
  showPointer?: boolean;
  /** Dynamischer Hinweis je nach Spielstand (überschreibt waitHint wenn gesetzt) */
  hint?: (snap: Snapshot) => string;
};

export type StoryGate = {
  active: boolean;
  hard: boolean;
  message: string;
  allowedPanels: Array<string | null>;
  lockCoachMinimize: boolean;
  /** Bei reinem Einsatz-Grind: kein gelber FAB links */
  hideStoryFab?: boolean;
  highlightTarget?: TourTarget;
  focusCompetenceId?: string;
  missionId?: string;
  autoOpenPanel?: "progress" | "citizen" | null;
};

/** Immer erreichbar — nie durch Story/Tutorial sperren */
export const META_ALWAYS_PANELS: Array<string | null> = [
  "settings",
  "appearance",
  "legal",
  "admin",
  "katalog-editor",
  "news",
  "faq",
  "help",
];

function withMetaPanels(panels: Array<string | null>): Array<string | null> {
  const set = new Set<string | null>([...panels, ...META_ALWAYS_PANELS]);
  return [...set];
}

function hasLs(s: Snapshot) {
  return hasLeitstelleOnMap(s);
}

function comps(s: Snapshot) {
  return s.world.competences ?? [];
}

export const STORY_MISSION_GUIDES: Record<string, StoryMissionGuideDef> = {
  ch1_leitstelle: {
    target: "build",
    action: "none",
    guideOnly: true,
    waitHint: "Zuweisung unterschreiben, dann unten „Bauen“ → Führung → BOS-Leitstelle setzen.",
    ready: hasLs,
  },
  ch1_feuer: {
    target: "build",
    action: "none",
    guideOnly: true,
    waitHint: "Wachen-Zuweisung unterschreiben, dann unten „Bauen“ → erste Einsatzwache platzieren.",
    ready: (s) =>
      s.buildings.some((b) =>
        ["fire_station", "police_station", "ems_station"].includes(b.kind),
      ),
  },
  ch1_besatzung: {
    target: "dock-station",
    action: "open_stations",
    guideOnly: true,
    waitHint: "Wachen → Ihre Wache → Besatzung → Personal zuweisen.",
    ready: (s) => s.personnel.some((p) => !!p.vehicleId),
  },
  ch1_erste_lagen: {
    target: "incident-list",
    action: "none",
    resolveGrind: true,
    waitHint: "Rechts in der Einsatzliste alarmieren und Lagen abschließen.",
    hint: (s) => {
      const cur = s.incidents.filter((i) => i.status === "resolved").length;
      const left = Math.max(0, 2 - cur);
      if (left > 0) {
        return `Noch ${left} Einsatz${left === 1 ? "" : "e"} abschließen — Alarmieren, Anfahrt abwarten, vor Ort erledigen.`;
      }
      return "Zwei Einsätze erledigt — Auftrag erfüllt.";
    },
    ready: (s) => s.incidents.filter((i) => i.status === "resolved").length >= 2,
  },
  ch1_nachalarm: {
    target: "stat-progress",
    action: "none",
    guideOnly: true,
    waitHint: "Level & Rang → Ast Alarmierung → „Weiter alarmieren“ freischalten.",
    hint: (s) => {
      const lvl = s.world.playerLevel ?? 1;
      const sp = (s.world as { skillPoints?: number }).skillPoints ?? 0;
      const inf = s.world.influence ?? 0;
      if (lvl < 2) {
        return "Noch Einsätze abschließen, bis Level 2 erreicht ist.";
      }
      if (comps(s).includes("alarm_re_dispatch")) {
        return "Kompetenz freigeschaltet — Auftrag erfüllt.";
      }
      if (sp < 1) {
        return "Skillpunkt fehlt — Seite neu laden (F5), dann freischalten.";
      }
      if (inf < 1) {
        return "Noch 1 Einfluss nötig, dann „Weiter alarmieren“ freischalten.";
      }
      return "Level & Rang → Alarmierung → „Weiter alarmieren“ (1 SP + 1 Einfluss).";
    },
    ready: (s) => comps(s).includes("alarm_re_dispatch"),
  },
  ch1_weiter_ueben: {
    target: "incident-list",
    action: "none",
    guideOnly: true,
    showPointer: false,
    waitHint:
      "Zwei Einsätze parallel: am zweiten eine Einheit vom ersten antippen → „Weiter alarmieren“.",
    ready: (s) => ((s.world.story as { redispatchCount?: number })?.redispatchCount ?? 0) >= 1,
  },
  ch1_fuhrpark: {
    target: "dock-station",
    action: "open_stations",
    guideOnly: true,
    waitHint: "Wachen → Fahrzeugshop → zweites Fahrzeug an derselben Wache kaufen.",
    ready: (s) => s.vehicles.length >= 2,
  },
  ch1_personal_werben: {
    target: "hr",
    action: "open_hr_recruit",
    guideOnly: true,
    waitHint: "Personal → Werbung → Kampagne starten.",
    hint: (s) => {
      const n = (s.world.story as { recruitCampaignCount?: number })?.recruitCampaignCount ?? 0;
      if (n >= 1) return "Werbung läuft — Auftrag erfüllt.";
      const now = Date.now();
      const pending = (s.applicants ?? []).filter(
        (a) => new Date(a.createdAt as string | number | Date).getTime() > now,
      ).length;
      const pool = (s.applicants ?? []).filter(
        (a) => new Date(a.createdAt as string | number | Date).getTime() <= now,
      ).length;
      if (pending > 0 && pool < 1) {
        return "Bewerber sind unterwegs — kurz warten, dann erscheinen sie im Tab Bewerber.";
      }
      if (pool > 0) return "Bewerber sind da — Werbung gilt als gestartet.";
      return "Personal → Tab Werbung → Aushang, Anzeige oder Kampagne wählen.";
    },
    ready: (s) => ((s.world.story as { recruitCampaignCount?: number })?.recruitCampaignCount ?? 0) >= 1,
  },
  ch1_personal_gespraech: {
    target: "hr",
    action: "open_hr_pool",
    guideOnly: true,
    waitHint: "Personal → Bewerber → Gespräch einladen (~1 Min.).",
    hint: (s) => {
      const done = (s.world.story as { interviewCount?: number })?.interviewCount ?? 0;
      if (done >= 1) return "Gespräch abgeschlossen — Auftrag erfüllt.";
      const interviewing = (s.applicants ?? []).some((a) => a.recruitStatus === "interviewing");
      if (interviewing) {
        return "Gespräch läuft — Ergebnis erscheint automatisch. Bis dahin Einsätze weiter bearbeiten.";
      }
      const pool = (s.applicants ?? []).filter((a) => (a.recruitStatus ?? "applied") === "applied").length;
      if (pool < 1) return "Noch keine Bewerber — zuerst Werbung unter Personal → Werbung.";
      return `${pool} Bewerber bereit — Gespräch einladen und kurz warten.`;
    },
    ready: (s) => ((s.world.story as { interviewCount?: number })?.interviewCount ?? 0) >= 1,
  },
  ch1_personal_einstellen: {
    target: "hr",
    action: "open_hr_pool",
    guideOnly: true,
    waitHint: "Bewerber nach abgeschlossenem Gespräch annehmen und einstellen.",
    hint: (s) => {
      const hired = (s.world.story as { hireCount?: number })?.hireCount ?? 0;
      if (hired >= 1) return "Personal eingestellt — Auftrag erfüllt.";
      const ready = (s.applicants ?? []).filter((a) => a.recruitStatus === "interview_done").length;
      if (ready < 1) {
        return "Noch kein abgeschlossenes Gespräch — Bewerber einladen und ~1 Min. warten.";
      }
      return `${ready} Kandidat${ready === 1 ? "" : "en"} bereit — „Annehmen & einstellen“ wählen.`;
    },
    ready: (s) => ((s.world.story as { hireCount?: number })?.hireCount ?? 0) >= 1,
  },
  ch1_besatzung_zweites_fz: {
    target: "hr",
    action: "open_hr_assign",
    guideOnly: true,
    waitHint: "Personal → Besatzung → neue Kraft dem zweiten Fahrzeug zuweisen.",
    hint: (s) => {
      const under = understaffedVehicleCount(s);
      if (under <= 0) return "Alle Fahrzeuge besetzt — Auftrag erfüllt.";
      return under === 1
        ? "Ein Fahrzeug unter Mindestbesatzung — Tab Besatzung → Wache wählen → Person dem LF zuweisen oder „Wache befüllen“."
        : `${under} Fahrzeuge unter Mindestbesatzung — Tab Besatzung, Wache wählen, dann „Wache befüllen“ oder manuell zuweisen.`;
    },
    ready: (s) => understaffedVehicleCount(s) <= 0,
  },
  ch1_zweite_wache: {
    target: "build",
    action: "none",
    guideOnly: true,
    waitHint: "Unten „Bauen“ → weitere BOS-Organisation (Polizei, RD oder THW).",
    hint: (s) => {
      const ops = s.buildings.filter((b) =>
        ["fire_station", "police_station", "ems_station", "thw"].includes(b.kind),
      ).length;
      const lvl = s.world.playerLevel ?? 1;
      if (lvl < 3) return "Noch Einsätze abschließen, bis Level 3 erreicht ist.";
      if (ops < 2) return `Noch ${2 - ops} Einsatzwache — Bauen → andere Organisation.`;
      return "Zwei Einsatzwachen — Auftrag erfüllt.";
    },
    ready: (s) => {
      const ops = s.buildings.filter((b) =>
        ["fire_station", "police_station", "ems_station", "thw"].includes(b.kind),
      ).length;
      return (s.world.playerLevel ?? 1) >= 3 && ops >= 2;
    },
  },
  ch1_zwischen_lagen: {
    target: "incident-list",
    action: "none",
    resolveGrind: true,
    waitHint: "Einsätze alarmieren und abschließen.",
    ready: (s) => s.incidents.filter((i) => i.status === "resolved").length >= 4,
  },
  ch2_rathaus_angebot: {
    target: "incident-list",
    action: "none",
    guideOnly: true,
    waitHint:
      "Kap. 2 Start: Fehlbestand-Dialog (IM/Rathaus) abwarten oder Ansehen ≥8 — Coach führt zum Gespräch.",
    ready: (s) => {
      const story = s.world.story as { fehlbestandCount?: number } | undefined;
      return (story?.fehlbestandCount ?? 0) >= 1 || (s.world.renown ?? 0) >= 8;
    },
  },
  ch2_im_fuhrpark: {
    target: "dock-station",
    action: "open_stations",
    guideOnly: true,
    waitHint:
      "Nach dem Rathaus: Fuhrpark auf ≥5 Fahrzeuge oder zweiten Fehlbestand abwarten — dann Polizei.",
    ready: (s) => {
      const story = s.world.story as { fehlbestandCount?: number } | undefined;
      return (story?.fehlbestandCount ?? 0) >= 2 || (s.vehicles?.length ?? 0) >= 5;
    },
  },
  ch2_polizei: {
    target: "build",
    action: "none",
    guideOnly: true,
    waitHint: "Unten „Bauen“ → Polizeiwache — Kap. 2 weitet die Orgs über die Feuerwehr hinaus.",
    ready: (s) => s.buildings.some((b) => b.kind === "police_station"),
  },
  ch2_polizei_lagen: {
    target: "incident-list",
    action: "none",
    resolveGrind: true,
    waitHint: "Drei Polizei-Einsätze alarmieren und abschließen.",
    ready: (s) =>
      ((s.world.story as { orgResolved?: { police?: number } })?.orgResolved?.police ?? 0) >= 3,
  },
  ch2_gewahrsam: {
    target: "funk-panel",
    action: "none",
    guideOnly: true,
    waitHint: "Nach Festnahme im Funk Gewahrsam/Zelle übernehmen.",
    ready: (s) =>
      ((s.world.story as { custodyClaimCount?: number })?.custodyClaimCount ?? 0) >= 1,
  },
  ch2_rettung: {
    target: "build",
    action: "none",
    guideOnly: true,
    waitHint: "Rettungswache bauen oder RD-Erweiterung an der Berufsfeuerwehr.",
    ready: (s) =>
      s.buildings.some((b) => b.kind === "ems_station") ||
      s.buildings.some((b) => {
        try {
          return (JSON.parse(b.modulesJson || "[]") as string[]).includes("fw_rd");
        } catch {
          return false;
        }
      }),
  },
  ch2_rettung_lagen: {
    target: "incident-list",
    action: "none",
    resolveGrind: true,
    waitHint: "Drei RD-Einsätze alarmieren und abschließen.",
    ready: (s) =>
      ((s.world.story as { orgResolved?: { ems?: number } })?.orgResolved?.ems ?? 0) >= 3,
  },
  ch2_netz: {
    target: "build",
    action: "none",
    guideOnly: true,
    waitHint: "Netz ausbauen — insgesamt mind. 4 Einsatzwachen (FW/Pol/RD/THW).",
    ready: (s) =>
      s.buildings.filter((b) =>
        ["fire_station", "police_station", "ems_station", "thw"].includes(b.kind),
      ).length >= 4,
  },
  ch1_org_anschub: {
    target: "incident-list",
    action: "none",
    resolveGrind: true,
    waitHint: "Einsätze alarmieren und abschließen — Nachweis für die nächste Organisation.",
    ready: (s) => s.incidents.filter((i) => i.status === "resolved").length >= 4,
  },
  ch1_funk: {
    target: "funk-panel",
    action: "none",
    ctaLabel: "Funk beachten",
    waitHint:
      "Status 5 / Sprechwunsch im Einsatz öffnen und eine Vor-Ort-Entscheidung wählen (Lage halten, Nachalarm, dokumentieren …).",
    ready: (s) => ((s.world.story as { radioAckCount?: number })?.radioAckCount ?? 0) >= 1,
  },
  ch1_taktik: {
    target: "incident-list",
    action: "open_incident",
    guideOnly: true,
    ctaLabel: "Einsatz öffnen",
    waitHint:
      "Kap. 2 — nach dem Netz: Tab Karte → Werkzeug (Verfolgung, Sammelstelle, Wasser, Absperrung). Disposition zuerst beherrschen.",
    hint: (s) => {
      const n = (s.world.story as { mapTacticalCount?: number })?.mapTacticalCount ?? 0;
      if (n >= 1) return "Karten-Einsatzführung genutzt — Auftrag erfüllt.";
      const inc = s.incidents.find(
        (i) =>
          (i.status === "open" || i.status === "active") &&
          /Verfolg|Fahndung|Groß|Volksfest|Wasser|VU|Seebrand|Wasserrettung|MANV|Massenanfall/i.test(
            i.title + (i.summary ?? ""),
          ),
      );
      const blob = inc ? `${inc.title} ${inc.summary ?? ""}` : "";
      if (/MANV|Massenanfall|Patientenablage|rd_manv/i.test(blob)) {
        return "MANV/RD: Patienten-Sammelstelle und Abschnitte auf der Karte — Triage im Einsatz verknüpfen.";
      }
      if (/Wasser|Seebrand|Hydrant|Saug/i.test(blob)) {
        return "Wasser-Einsatz: Hydrant oder Saugstelle auf der Karte — bei Druckmangel nur Saugstelle/Pendel.";
      }
      if (/Wasserrettung|Ufer|THW/i.test(blob)) {
        return "Wasserrettung: Sammelstelle, Abschnitt oder Saugstelle auf der Karte.";
      }
      if (/Verfolg|Fahndung|Flucht/i.test(blob)) {
        return "Verfolgung: Nagelgurt oder Sperre vor dem Flüchtenden platzieren.";
      }
      if (/Groß|Volksfest|Evakuierung|Schulbrand/i.test(blob)) {
        return "Großlage/Lage: Kräfte-Sammelstelle oder Evakuierungsbereich markieren.";
      }
      if (/VU|Absperr|Autobahn/i.test(blob)) {
        return "Absperrung: Sperrstelle oder Verkehrskontrollpunkt auf der Karte.";
      }
      return "Einmal eine Karten-Maßnahme setzen (Profil-abhängig).";
    },
    ready: (s) => ((s.world.story as { mapTacticalCount?: number })?.mapTacticalCount ?? 0) >= 1,
  },
  ch1_zweite_eigene: {
    target: "build",
    action: "none",
    guideOnly: true,
    waitHint: "Unten „Bauen“ → zweite Wache der eigenen Organisation oder dritte Einsatzwache.",
    ready: (s) => {
      const ops = s.buildings.filter((b) =>
        ["fire_station", "police_station", "ems_station", "thw"].includes(b.kind),
      ).length;
      return (s.world.playerLevel ?? 1) >= 4 && ops >= 3;
    },
  },
  ch1_nachforderungen: {
    target: "incident-list",
    action: "none",
    guideOnly: true,
    showPointer: false,
    waitHint:
      "Abschlepp-Vertrag unterschreiben. Dann je einmal anfordern: Abschlepper, Straßenreinigung, Absperrung, Umleitung, Behelfsampel.",
    ready: (s) => {
      const c = (s.world.story as { supportCounts?: Record<string, number> })?.supportCounts ?? {};
      return (
        (c.tow ?? 0) >= 1 &&
        (c.street_clean ?? 0) >= 1 &&
        (c.roadblock ?? 0) >= 1 &&
        (c.detour ?? 0) >= 1 &&
        (c.traffic_light ?? 0) >= 1
      );
    },
  },
  ch1_abschlepper: {
    target: "incident-list",
    action: "none",
    guideOnly: true,
    showPointer: false,
    waitHint:
      "Abschlepp-Vertrag unterschreiben. Dann je einmal anfordern: Abschlepper, Straßenreinigung, Absperrung, Umleitung, Behelfsampel.",
    ready: (s) => {
      const c = (s.world.story as { supportCounts?: Record<string, number> })?.supportCounts ?? {};
      return (
        (c.tow ?? 0) >= 1 &&
        (c.street_clean ?? 0) >= 1 &&
        (c.roadblock ?? 0) >= 1 &&
        (c.detour ?? 0) >= 1 &&
        (c.traffic_light ?? 0) >= 1
      );
    },
  },
  ch2_foerderung: {
    target: "citizen-grant",
    action: "open_citizen",
    ctaLabel: "Förderbescheid öffnen",
    waitHint: "Rahmenvertrag und Antrag unterschreiben. Danach drei Einsätze — dann Auszahlung.",
    ready: (s) => {
      const st = s.world.story as { grantsCompleted?: number; grant?: { status?: string } };
      return (st?.grantsCompleted ?? 0) >= 1 || st?.grant?.status === "done";
    },
  },
  ch3_rw: {
    target: "dock-station",
    action: "open_stations",
    guideOnly: true,
    waitHint: "Fuhrpark: Rüstwagen (RW) an einer Feuerwache beschaffen.",
    ready: (s) => (s.vehicles ?? []).some((v) => v.type === "RW" || v.type.startsWith("RW")),
  },
  ch3_schlauch: {
    target: "dock-station",
    action: "open_stations",
    guideOnly: true,
    waitHint: "Schlauchwagen beschaffen (Wasserversorgung / lange Förderstrecken).",
    ready: (s) =>
      (s.vehicles ?? []).some((v) => v.type === "SchlauchW" || /Schlauch/i.test(v.type)),
  },
  ch3_tlf: {
    target: "dock-station",
    action: "open_stations",
    guideOnly: true,
    waitHint: "TLF beschaffen — Löschwasser-Reserve für Außerorts und BAB.",
    ready: (s) => (s.vehicles ?? []).some((v) => v.type === "TLF" || v.type.startsWith("TLF")),
  },
  ch3_oel: {
    target: "incident-list",
    action: "none",
    guideOnly: true,
    waitHint: "GW-Öl kaufen oder einmal Straßenreinigung an einem Ölspur-/VU-Einsatz anfordern.",
    ready: (s) => {
      if ((s.vehicles ?? []).some((v) => /GW-Oel|GW-Öl/i.test(v.type))) return true;
      const c = (s.world.story as { supportCounts?: Record<string, number> })?.supportCounts ?? {};
      return (c.street_clean ?? 0) >= 1;
    },
  },
  ch3_elw1: {
    target: "dock-station",
    action: "open_stations",
    guideOnly: true,
    waitHint: "ELW 1 beschaffen — Führung vor Ort für Zug und Abschnitt.",
    ready: (s) =>
      (s.vehicles ?? []).some((v) => v.type === "ELW1" || v.type === "ELW" || v.type === "ELW2"),
  },
  ch3_hlf: {
    target: "dock-station",
    action: "open_stations",
    guideOnly: true,
    waitHint: "HLF beschaffen — Löschen und technische Hilfe in einem Fahrzeug.",
    ready: (s) => (s.vehicles ?? []).some((v) => v.type === "HLF" || v.type.startsWith("HLF")),
  },
  ch3_krankenhaus: {
    target: "build",
    action: "none",
    guideOnly: true,
    waitHint: "Krankenhaus bauen und mind. 4 Betten sicherstellen (Module/Ausbau).",
    ready: (s) => {
      const kh = s.buildings.filter((b) => b.subtype === "hospital" || b.kind === "hospital");
      const beds = kh.reduce((n, b) => n + (b.slots || 0), 0);
      return kh.length > 0 && beds >= 4;
    },
  },
  ch3_elw2: {
    target: "dock-station",
    action: "open_stations",
    guideOnly: true,
    waitHint: "ELW 2 beschaffen — Stab und größere Abschnitte.",
    ready: (s) => (s.vehicles ?? []).some((v) => v.type === "ELW2"),
  },
  ch3_ansehen: {
    target: "incident-list",
    action: "none",
    guideOnly: true,
    waitHint:
      "Ansehen auf 60 — Einsätze sauber abschließen. Optional: Finanzen → Ansehen → Image-Kampagne für Bewerber.",
    ready: (s) => (s.world.renown ?? 0) >= 60,
  },
  ch3_thw: {
    target: "build",
    action: "none",
    guideOnly: true,
    waitHint: "THW-Standort früher anbinden (Bauen → THW) — parallel zum Fuhrpark.",
    ready: (s) => s.buildings.some((b) => b.kind === "thw"),
  },
  ch4_nachbar: {
    target: "incident-list",
    action: "none",
    resolveGrind: true,
    waitHint:
      "Level 12 erreichen — dann Nachbarkreis: Spawn, Cross-Alarm und Bau (Skills bleiben, Wachen neu).",
    ready: (s) => (s.world.playerLevel ?? 1) >= 12,
  },
  ch4_jva: {
    target: "build",
    action: "none",
    guideOnly: true,
    waitHint:
      "Nach Nachbar+Bahn: JVA ab Level 16 — Polizei + Gewahrsam üben (3×) oder Kompetenz „Gewahrsam auto“ — Bauen → Polizei.",
    ready: (s) =>
      (s.world.playerLevel ?? 1) >= 16 && s.buildings.some((b) => b.kind === "jva"),
  },
  ch5_nachbar_wachen: {
    target: "build",
    action: "none",
    guideOnly: true,
    waitHint: "Erste Ops-Wache im tealen Nachbarkreis bauen (Grenze muss freigeschaltet sein).",
    ready: (s) =>
      ((s.world.story as { stationsInNeighbor?: number } | undefined)?.stationsInNeighbor ?? 0) >= 1,
  },
  ch5_flotte: {
    target: "dock-station",
    action: "open_stations",
    guideOnly: true,
    waitHint: "Mindestens 12 Fahrzeuge im Fuhrpark.",
    ready: (s) => (s.vehicles ?? []).length >= 12,
  },
  ch5_schule: {
    target: "build",
    action: "none",
    guideOnly: true,
    waitHint: "Ausbildungsstätte bauen (Feuerwehr-/Polizei-/RD-/THW-Schule).",
    ready: (s) => s.buildings.some((b) => b.kind === "school"),
  },
  ch5_lage: {
    target: "incident-list",
    action: "none",
    resolveGrind: true,
    waitHint: "7 Wachen, Ansehen ≥ 70, Level ≥ 12 — überörtliche Bereitschaft.",
    ready: (s) => {
      const stations = s.buildings.filter((b) =>
        ["fire_station", "police_station", "ems_station", "thw"].includes(b.kind),
      ).length;
      return stations >= 7 && (s.world.renown ?? 0) >= 70 && (s.world.playerLevel ?? 1) >= 12;
    },
  },
  ch5_bahn: {
    target: "build",
    action: "none",
    guideOnly: true,
    waitHint:
      "Güterbahnhof oder Modul Bahnanschluss in Heimat und Nachbarkreis — dann Bahn-Transfer nutzbar.",
    ready: (s) =>
      Boolean((s.world.story as { railZonesReady?: boolean } | undefined)?.railZonesReady),
  },
  ch6_landesnetz: {
    target: "incident-list",
    action: "none",
    resolveGrind: true,
    waitHint: "Level ≥ 14, Ansehen ≥ 75, mind. 8 Wachen.",
    ready: (s) => {
      const stations = s.buildings.filter((b) =>
        ["fire_station", "police_station", "ems_station", "thw"].includes(b.kind),
      ).length;
      return stations >= 8 && (s.world.renown ?? 0) >= 75 && (s.world.playerLevel ?? 1) >= 14;
    },
  },
  ch6_verband_prep: {
    target: "build",
    action: "none",
    guideOnly: true,
    waitHint: "Nachbar-Wache und Bahn-Terminals (Heimat + Nachbar) — Level ≥ 14.",
    ready: (s) =>
      (s.world.playerLevel ?? 1) >= 14 &&
      ((s.world.story as { stationsInNeighbor?: number } | undefined)?.stationsInNeighbor ?? 0) >=
        1 &&
      Boolean((s.world.story as { railZonesReady?: boolean } | undefined)?.railZonesReady),
  },
};

export function getStoryMissionGuide(missionId: string | undefined): StoryMissionGuideDef | null {
  if (!missionId) return null;
  return STORY_MISSION_GUIDES[missionId] ?? null;
}

export function missionGuideHint(
  guide: StoryMissionGuideDef | null,
  snap: Snapshot,
): string | null {
  if (!guide) return null;
  if (guide.hint) return guide.hint(snap);
  return guide.waitHint ?? null;
}

export function deriveStoryGate(snapshot: Snapshot | null): StoryGate {
  const open: StoryGate = {
    active: false,
    hard: false,
    message: "",
    allowedPanels: [],
    lockCoachMinimize: false,
  };
  if (!snapshot) return open;

  const story = snapshot.world.story;
  const active = story?.active;
  const grant = story?.grant;
  const level = snapshot.world.playerLevel ?? 1;
  const renown = snapshot.world.renown ?? 0;
  const influence = snapshot.world.influence ?? 0;
  const grantsCompleted =
    (story as { grantsCompleted?: number } | undefined)?.grantsCompleted ?? 0;

  if (grant?.status === "active") {
    const resolved = (snapshot.incidents ?? []).filter((i) => i.status === "resolved").length;
    const done = Math.min(
      grant.needIncidents,
      Math.max(0, resolved - (grant.baselineResolved ?? 0)),
    );
    const left = Math.max(0, (grant.needIncidents ?? 3) - done);
    return {
      active: true,
      // Kein Hard-Gate / kein gelber Puls-FAB — Spieler soll normal disponieren
      hard: false,
      hideStoryFab: true,
      message: `Rathaus-Förderung: noch ${left} Einsatz${left === 1 ? "" : "e"} — dann ${grant.payout.toLocaleString("de-DE")} €.`,
      allowedPanels: withMetaPanels([
        null,
        "incident",
        "citizen",
        "station",
        "build",
        "hr",
        "finance",
        "progress",
        "verwaltung",
      ]),
      lockCoachMinimize: false,
      highlightTarget: "incident-list",
      missionId: active?.id,
    };
  }

  if (active?.id === "ch1_nachalarm" && !comps(snapshot).includes("alarm_re_dispatch")) {
    if (level < 2) {
      return {
        active: true,
        hard: false,
        hideStoryFab: true,
        message: "Noch Level 2 erreichen — dann „Weiter alarmieren“ freischalten.",
        allowedPanels: withMetaPanels([
          null,
          "incident",
          "progress",
          "station",
          "citizen",
          "finance",
          "build",
          "catalog",
          "hr",
          "rewards",
          "verwaltung",
          "verband",
        ]),
        lockCoachMinimize: false,
        highlightTarget: "incident-list",
        missionId: active.id,
      };
    }
    return {
      active: true,
      hard: false,
      hideStoryFab: false,
      message: "Level & Rang → Alarmierung → „Weiter alarmieren“ freischalten.",
      allowedPanels: withMetaPanels([null, "progress", "incident", "station", "build", "hr", "finance"]),
      lockCoachMinimize: false,
      highlightTarget: "stat-progress",
      focusCompetenceId: "alarm_re_dispatch",
      missionId: active.id,
    };
  }

  if (
    active?.id === "ch2_foerderung" &&
    grantsCompleted < 1 &&
    grant?.status !== "done" &&
    renown >= 35 &&
    influence >= 6
  ) {
    return {
      active: true,
      hard: true,
      message: "Rahmenvertrag und Förderantrag unterschreiben. Danach drei Einsätze — Auszahlung am Ende.",
      allowedPanels: withMetaPanels([null, "citizen", "incident"]),
      lockCoachMinimize: true,
      highlightTarget: "citizen-grant",
      missionId: active.id,
    };
  }

  return open;
}

export function isPanelAllowed(snapshot: Snapshot | null, panel: string | null): boolean {
  if (panel == null) return true;
  if (META_ALWAYS_PANELS.includes(panel)) return true;
  const gate = deriveStoryGate(snapshot);
  if (!gate.hard) return true;
  return gate.allowedPanels.includes(panel);
}
